import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { WEEKDAYS, ROOMS } from '@/lib/education-levels';

interface RoomAvailabilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  defaultWeekday?: number;
}

function normalizeRoomName(name: string | null | undefined): string {
  if (!name) return '';
  // normalize "room 1", "Room  1", "ROOM1" -> "room 1"
  return name.trim().toLowerCase().replace(/\s+/g, ' ');
}

export function RoomAvailabilityDialog({
  open,
  onOpenChange,
  defaultWeekday = 1,
}: RoomAvailabilityDialogProps) {
  const [weekday, setWeekday] = useState(String(defaultWeekday));
  const [startTime, setStartTime] = useState('09:00');
  const [endTime, setEndTime] = useState('11:00');

  const { data: templates = [], isFetching } = useQuery({
    queryKey: ['room-availability', weekday],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('v_class_templates')
        .select('room_name, start_time, end_time')
        .eq('weekday', Number(weekday))
        .eq('is_active', true)
        .not('room_name', 'is', null);
      if (error) throw error;
      return (data ?? []) as { room_name: string; start_time: string; end_time: string }[];
    },
    enabled: open,
  });

  const busyRoomNames = useMemo(() => {
    const busy = new Set<string>();
    for (const t of templates) {
      // overlap: t.start < end AND t.end > start
      if (t.start_time < endTime && t.end_time > startTime) {
        busy.add(normalizeRoomName(t.room_name));
      }
    }
    return busy;
  }, [templates, startTime, endTime]);

  const rooms = useMemo(() => {
    return ROOMS.map((room) => {
      const normalized = normalizeRoomName(room.value);
      return { ...room, available: !busyRoomNames.has(normalized) };
    });
  }, [busyRoomNames]);

  const availableCount = rooms.filter((r) => r.available).length;

  const timeValid = startTime && endTime && startTime < endTime;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>ตรวจสอบห้องว่าง</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-4 pt-2">
          <div>
            <Label>วัน</Label>
            <Select value={weekday} onValueChange={setWeekday}>
              <SelectTrigger className="mt-1.5">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WEEKDAYS.map((day) => (
                  <SelectItem key={day.key} value={day.key}>
                    {day.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>เวลาเริ่ม</Label>
            <Input
              type="time"
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="mt-1.5"
            />
          </div>
          <div>
            <Label>เวลาจบ</Label>
            <Input
              type="time"
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="mt-1.5"
            />
          </div>
        </div>

        {!timeValid && startTime && endTime && (
          <p className="text-sm text-destructive">เวลาเริ่มต้องน้อยกว่าเวลาจบ</p>
        )}

        {timeValid && (
          <div className="flex-1 overflow-y-auto mt-4 space-y-3 pb-2">
            <div className="flex items-center gap-3 text-sm text-muted-foreground">
              {isFetching ? (
                <span>กำลังโหลด...</span>
              ) : (
                <>
                  <span>
                    ว่าง{' '}
                    <span className="font-semibold text-green-600">{availableCount}</span> ห้อง
                  </span>
                  <span>·</span>
                  <span>
                    ไม่ว่าง{' '}
                    <span className="font-semibold text-red-500">
                      {90 - availableCount}
                    </span>{' '}
                    ห้อง
                  </span>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <Badge variant="outline" className="border-green-500 text-green-600 text-xs">ว่าง</Badge>
                    <Badge variant="outline" className="border-red-400 text-red-500 text-xs">ไม่ว่าง</Badge>
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-6 gap-1.5">
              {rooms.map((room) => (
                <div
                  key={room.value}
                  className={`rounded-md border px-2 py-1.5 text-center text-sm font-medium transition-colors ${
                    room.available
                      ? 'border-green-400 bg-green-50 text-green-700'
                      : 'border-red-300 bg-red-50 text-red-600'
                  }`}
                >
                  {room.label}
                </div>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
